import { useSyncExternalStore } from 'react';
import { estEffetDeFiche, typeDeFichierAccepte } from '@taki/data-model';
import type { AppScreenAction, FichiersDeJeu } from '@taki/data-model';
import type { MarqueDeDocument } from '@taki/pdf';
import type { TakiSdk } from '@taki/sdk';
import { useBrandingPort } from '@taki/ui';
import type { ScreenAction, ViewBinding } from '@taki/ui';
import { cleDExport, exporterEnPdf, ficheEnHtml, RAISON_PDF, suiviDesExports, type EtatDExport } from './fiche-en-pdf.js';

/**
 * LES GESTES DE FICHE : les actions declarees qui nomment un EFFET DE FICHE
 * (`EFFETS_DE_FICHE`, `@taki/data-model`), rendues dans le menu « ... » de la
 * fiche ouverte. La rangee 3a ne se rend qu'au niveau collection ; un geste qui
 * porte sur UNE fiche n'a de sens que la fiche ouverte.
 *
 * Le seul aujourd'hui, `socle:exporterPdf` : la fiche, telle que sa vue
 * `core.fiche` la montre (cartes de champs, libelles, valeurs de catalogue),
 * rendue en HTML a la marque de l'org (`@taki/pdf`), convertie par le serveur
 * (`sdk.files.genererPdf`) et rangee dans ses pieces jointes. Contrat :
 * `docs/PDF.md`.
 *
 * L'AVANCEMENT SE LIT DANS LA CARTE DES FICHIERS, pas dans le menu : le menu se
 * referme au clic, et c'est dans les pieces jointes que le PDF va apparaitre.
 * Le suivi est donc un petit magasin partage (`suiviDesExports`,
 * `fiche-en-pdf.ts`) que la carte (`FilesPanel`) et le menu lisent tous deux.
 */

/** L'etat de l'export de cette fiche, relu a chaque changement. */
export function useEtatDExport(cle: string | null): EtatDExport | undefined {
  return useSyncExternalStore(
    suiviDesExports.abonner,
    () => (cle === null ? undefined : suiviDesExports.lire(cle)),
  );
}

export interface OptionsDesGestesDeFiche<T> {
  sdk: TakiSdk;
  /** Les actions DECLAREES de la porte active ; seules celles qui nomment un effet de fiche comptent. */
  actions: ReadonlyArray<Pick<AppScreenAction, 'id' | 'label' | 'fn'>> | undefined;
  /** Le nom de la porte active. */
  porte: string;
  jeu: { family: string; type?: string | undefined } | null;
  politique: FichiersDeJeu | null;
  /** L'id de la fiche ouverte, `null` quand ce n'est pas une fiche en base. */
  ficheOuverte: string | null;
  record: T | undefined;
  binding: ViewBinding<T> | undefined;
  /** La raison pour laquelle la porte ne s'ecrit pas (`useScreen().raisonAcces`). */
  raisonAcces: string | null;
}

/**
 * Les entrees du menu « ... » de la fiche ouverte que la declaration demande.
 * A AJOUTER a celles du moteur : `recordMenuActions={[...screen.recordMenuActions, ...gestes]}`.
 * Une entree qui ne peut pas servir reste VISIBLE, eteinte, avec sa raison (G16).
 */
export function useGestesDeFiche<T>(options: OptionsDesGestesDeFiche<T>): ScreenAction[] {
  const marqueCourante = useBrandingPort()?.courant;
  const { sdk, jeu, ficheOuverte, record, binding, politique } = options;
  const cle = jeu !== null && ficheOuverte !== null ? cleDExport(jeu.family, ficheOuverte) : null;
  const suivi = useEtatDExport(cle);
  if (jeu === null || ficheOuverte === null || record === undefined || binding === undefined) return [];
  return (options.actions ?? [])
    .filter((action) => estEffetDeFiche(action.fn))
    .map((action): ScreenAction => {
      const raison = options.raisonAcces
        ?? (politique === null ? RAISON_PDF.fichiersFermes
          : !typeDeFichierAccepte(politique, 'application/pdf') ? RAISON_PDF.pdfRefuse
          : suivi?.etat === 'enCours' ? RAISON_PDF.enCours
          : null);
      return {
        id: action.id,
        label: suivi?.etat === 'enCours' ? 'PDF en cours...' : action.label,
        variant: 'secondary',
        disabled: raison !== null,
        ...(raison === null ? {} : { disabledReason: raison }),
        onClick: () => {
          const marque: MarqueDeDocument = {
            nom: marqueCourante?.name ?? 'MyTaki',
            logo: marqueCourante?.logo ?? null,
            accent: marqueCourante?.accent ?? null,
          };
          const document = ficheEnHtml({ marque, porte: options.porte, binding, record, id: ficheOuverte });
          void exporterEnPdf(sdk, { collection: jeu.family, id: ficheOuverte }, document);
        },
      };
    });
}
